import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import { Layers, FolderOpen, Users, LogOut, User, ChevronDown, Shield, FileText, Activity, ClipboardList, Key, ScanLine, KeyRound, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";

const Navbar = () => {
  const navigate = useNavigate();
  const { user, isAdmin, logout, isAuthenticated, canEdit, changePassword } = useAuth();

  const [cpOpen, setCpOpen] = useState(false);
  const [cpCur, setCpCur] = useState("");
  const [cpNew, setCpNew] = useState("");
  const [cpNew2, setCpNew2] = useState("");
  const [cpBusy, setCpBusy] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const handleChangePassword = async () => {
    if (cpNew.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (cpNew !== cpNew2) { toast.error("Passwords do not match"); return; }
    setCpBusy(true);
    try {
      await changePassword(cpCur, cpNew);
      toast.success("Password changed");
      setCpOpen(false);
      setCpCur(""); setCpNew(""); setCpNew2("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change password");
    } finally { setCpBusy(false); }
  };

  return (
    <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-100 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link 
            to={isAdmin ? "/" : "/projects"} 
            className="flex items-center gap-3 hover:opacity-80 transition-opacity"
            data-testid="navbar-logo"
          >
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center shadow-md">
              <Layers className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading text-xl font-bold text-gray-900">
              Support<span className="text-blue-600">IQ</span>
            </span>
          </Link>

          <nav className="flex items-center gap-4">
            {isAuthenticated && (
              <>
                {/* Admin-only navigation */}
                {isAdmin && (
                  <>
                    <Link 
                      to="/uploads" 
                      className="hidden sm:flex items-center gap-2 text-sm text-gray-600 hover:text-amber-600 transition-colors font-medium"
                      data-testid="nav-uploads"
                    >
                      <FileText className="w-4 h-4" />
                      Recent Uploads
                    </Link>
                    <Link 
                      to="/admin/users" 
                      className="hidden sm:flex items-center gap-2 text-sm text-gray-600 hover:text-purple-600 transition-colors font-medium"
                      data-testid="nav-admin-users"
                    >
                      <Users className="w-4 h-4" />
                      Users
                    </Link>
                    <Link 
                      to="/admin/stats" 
                      className="hidden sm:flex items-center gap-2 text-sm text-gray-600 hover:text-emerald-600 transition-colors font-medium"
                      data-testid="nav-admin-stats"
                    >
                      <Activity className="w-4 h-4" />
                      System
                    </Link>
                    <Link 
                      to="/admin/joints" 
                      className="hidden sm:flex items-center gap-2 text-sm text-gray-600 hover:text-indigo-600 transition-colors font-medium"
                      data-testid="nav-admin-joints"
                    >
                      <ClipboardList className="w-4 h-4" />
                      Joints
                    </Link>
                  </>
                )}

                {/* Saved Batches - visible to admin and regular users */}
                {canEdit && (
                  <Link 
                    to="/projects" 
                    className="hidden sm:flex items-center gap-2 text-sm text-gray-600 hover:text-blue-600 transition-colors font-medium"
                    data-testid="nav-projects"
                  >
                    <FolderOpen className="w-4 h-4" />
                    Saved Batches
                  </Link>
                )}

                {/* Scan-IQ - visible to all authenticated users */}
                <Link 
                  to="/scan-iq" 
                  className="hidden sm:flex items-center gap-2 text-sm text-gray-600 hover:text-teal-600 transition-colors font-medium"
                  data-testid="nav-scan-iq"
                >
                  <ScanLine className="w-4 h-4" />
                  Scan-IQ
                </Link>

                {/* User Menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      className="flex items-center gap-2 h-9 px-3"
                      data-testid="user-menu-trigger"
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        isAdmin ? 'bg-purple-100' : 'bg-blue-100'
                      }`}>
                        {isAdmin ? (
                          <Shield className="w-4 h-4 text-purple-600" />
                        ) : (
                          <User className="w-4 h-4 text-blue-600" />
                        )}
                      </div>
                      <span className="hidden sm:inline text-sm font-medium text-gray-700">
                        {user?.name?.split(' ')[0]}
                      </span>
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel>
                      <div className="flex flex-col">
                        <span className="font-medium">{user?.name}</span>
                        <span className="text-xs text-gray-500 font-normal">@{user?.username}</span>
                        <span className={`text-xs mt-1 ${isAdmin ? 'text-purple-600' : 'text-blue-600'}`}>
                          {isAdmin ? 'Administrator' : 'User'}
                        </span>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {isAdmin && (
                      <>
                        <DropdownMenuItem 
                          className="sm:hidden"
                          onClick={() => navigate('/uploads')}
                        >
                          <FileText className="w-4 h-4 mr-2" />
                          Recent Uploads
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          className="sm:hidden"
                          onClick={() => navigate('/admin/users')}
                        >
                          <Users className="w-4 h-4 mr-2" />
                          User Management
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          className="sm:hidden"
                          onClick={() => navigate('/admin/stats')}
                        >
                          <Activity className="w-4 h-4 mr-2" />
                          System Health
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          className="sm:hidden"
                          onClick={() => navigate('/admin/joints')}
                          data-testid="nav-admin-joints-mobile"
                        >
                          <ClipboardList className="w-4 h-4 mr-2" />
                          Joint Report
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => navigate('/admin/api-keys')}
                          data-testid="nav-admin-api-keys"
                        >
                          <Key className="w-4 h-4 mr-2" />
                          API Keys
                        </DropdownMenuItem>
                      </>
                    )}
                    {canEdit && (
                      <DropdownMenuItem 
                        className="sm:hidden"
                        onClick={() => navigate('/projects')}
                      >
                        <FolderOpen className="w-4 h-4 mr-2" />
                        Saved Batches
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem 
                      className="sm:hidden"
                      onClick={() => navigate('/scan-iq')}
                      data-testid="nav-scan-iq-mobile"
                    >
                      <ScanLine className="w-4 h-4 mr-2" />
                      Scan-IQ
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="sm:hidden" />
                    <DropdownMenuItem
                      onClick={() => setCpOpen(true)}
                      data-testid="change-password-btn"
                    >
                      <KeyRound className="w-4 h-4 mr-2" />
                      Change Password
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={handleLogout}
                      className="text-red-600 focus:text-red-600"
                      data-testid="logout-btn"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
          </nav>
        </div>
      </div>

      <Dialog open={cpOpen} onOpenChange={setCpOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
            <DialogDescription>Enter your current password and choose a new one.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="cp-cur">Current password</Label>
              <Input id="cp-cur" type="password" value={cpCur} onChange={(e) => setCpCur(e.target.value)} data-testid="cp-current" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cp-new">New password</Label>
              <Input id="cp-new" type="password" value={cpNew} onChange={(e) => setCpNew(e.target.value)} data-testid="cp-new" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cp-new2">Confirm new password</Label>
              <Input id="cp-new2" type="password" value={cpNew2} onChange={(e) => setCpNew2(e.target.value)} data-testid="cp-new2" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCpOpen(false)}>Cancel</Button>
            <Button onClick={handleChangePassword} disabled={cpBusy} className="bg-blue-600 hover:bg-blue-700" data-testid="cp-submit">
              {cpBusy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Update Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </header>
  );
};

export default Navbar;
